import { useCallback, useEffect, useMemo, useState } from "react";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Space, Tag, message, Table, Typography } from "antd";
import AppContent from "../../../common/AppContent";
import AppPageHeader from "../../../common/PageHeader";
import ExtraContent from "../../Configuration/CommonComponents/ExtraContent";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";
import { getData } from "../../../apiServices/common";
import {
  CGL_AUDIT_EXPORT_REPORT_ENDPOINT,
  CGL_AUDIT_LIST_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { useLocation, useNavigate } from "react-router";
import { cglFormatDate } from "utils/functions";
import moment from "moment";
import ListTable from "common/ListTable";

const AuditReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location ? location.pathname : null;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilters, setActiveFilters] = useState({});
  const [exportLoading, setExportLoading] = useState(false);

  const { Title } = Typography;

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const getAuditReportList = useCallback(
    async (filterValues = {}) => {
      setLoading(true);

      const isFilterUpdate =
        "start_date" in filterValues ||
        "end_date" in filterValues ||
        "line_id" in filterValues;

      const isClear = Object.keys(filterValues).length === 0;

      let filters = activeFilters;
      if (isClear) {
        filters = {};
        setActiveFilters({});
        setCurrentPage(1);
      } else if (isFilterUpdate) {
        filters = Object.fromEntries(
          Object.entries({
            start_date: filterValues.start_date || null,
            end_date: filterValues.end_date || null,
            line_id: filterValues.line_id || null,
          }).filter(([, value]) => value != null),
        );
        setActiveFilters(filters);
        setCurrentPage(1);
      }

      const bodyData = {
        page: filterValues.page || (isFilterUpdate || isClear ? 1 : currentPage),
        per_page: filterValues.per_page || 10,
        ...filters,
      };

      const response = await getData(CGL_AUDIT_LIST_ENDPOINT, false, bodyData);

      if (response?.data?.code === 200) {
        setMetaData({
          defaultPageSize: response?.data?.data?.per_page,
          current: response?.data?.data?.current_page,
          defaultCurrent: response?.data?.data?.current_page,
          total: response?.data?.data?.total,
        });
        setTableData(response?.data?.data?.data || []);
      } else {
        message.error(
          response?.data?.message || "Error getting Audit Report data",
        );
      }

      setLoading(false);
    },
    [activeFilters, currentPage],
  );

  useEffect(() => {
    getAuditReportList({ page: currentPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    getAuditReportList({ page: newPage });
  };

  const handleViewDetails = (record) => {
    navigate("/digitalization/audit-report/view", {
      state: {
        audit_date: moment(record?.audit_date).format("YYYY-MM-DD"),
        line_id: record?.line_id,
        buyer_id: record?.buyer_id || null,
        sub_contract_id: record?.sub_contract_id || null,
        style_id: record?.style_id,
      },
    });
  };

  const handleExportExcel = async () => {
    if (!activeFilters?.start_date || !activeFilters?.end_date) {
      message.warning("Please select Start Date and End Date before downloading.");
      return;
    }

    setExportLoading(true);

    const response = await getData(
      CGL_AUDIT_EXPORT_REPORT_ENDPOINT,
      false,
      activeFilters,
      { "Content-Type": "blob" },
      "arraybuffer",
    );

    if (response?.data) {
      const outputFilename = `audit-report_${activeFilters.start_date}_to_${activeFilters.end_date}.xlsx`;
      const url = URL.createObjectURL(
        new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", outputFilename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      message.success("Audit Report downloaded successfully");
    } else {
      message.error("Failed to download Audit Report");
    }

    setExportLoading(false);
  };

  const tableColumns = [
    {
      title: "Audit Date",
      dataIndex: "audit_date",
      key: "audit_date",
      render: (value) => cglFormatDate(value),
    },
    {
      title: "Line",
      dataIndex: ["line_info", "name"],
      key: "line",
    },
    {
      title: "Buyer",
      key: "buyer",
      render: (_, record) =>
        record?.buyer?.name || record?.sub_contract?.name || "N/A",
    },
    {
      title: "Style",
      dataIndex: ["style_info", "style_no"],
      key: "style",
    },
    {
      title: "AQL",
      dataIndex: "aql",
      key: "aql",
    },
    {
      title: "Lot Qty",
      dataIndex: "lot_qty",
      key: "lot_qty",
    },
    {
      title: "Sample Qty",
      dataIndex: "sample_qty",
      key: "sample_qty",
    },
    {
      title: "Number of Audit",
      dataIndex: "number_of_audit",
      key: "number_of_audit",
      render: (value) => (value ? `Audit #${value}` : "N/A"),
    },
    {
      title: "Auditor",
      dataIndex: ["auditor", "name"],
      key: "auditor",
      render: (value) => value || "N/A",
    },
    {
      title: "Pass/Fail",
      dataIndex: "pass_fail",
      key: "pass_fail",
      render: (value) =>
        value ? (
          <Tag color={value === "Pass" ? "green" : "red"}>{value}</Tag>
        ) : (
          "N/A"
        ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 80,
      render: (_, record) => (
        <Typography.Link onClick={() => handleViewDetails(record)}>
          View
        </Typography.Link>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={getAuditReportList}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="audit_report"
      />
    ),
    [showFilter],
  );

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <Space>
              <Button
                type="primary"
                size="small"
                icon={<DownloadOutlined />}
                loading={exportLoading}
                onClick={handleExportExcel}
              >
                Download Excel
              </Button>
              <ExtraContent
                refresh={refresh}
                setRefresh={setRefresh}
                hideAddButton={true}
                showFilter={showFilter}
                setShowFilter={setShowFilter}
              />
            </Space>
          }
          title={<Title level={5}>Audit Report</Title>}
        />
      }
      content={
        <>
          {memoizedCustomFilter}

          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              loading={loading}
              dataSource={tableData}
              columns={tableColumns}
              rowKey={(record) => record.id}
              pagination={{
                showSizeChanger: false,
                defaultPageSize: metaData?.defaultPageSize,
                current: metaData?.current,
                onChange: (newPage) => {
                  changePage(newPage);
                },
                defaultCurrent: metaData?.defaultCurrent,
                total: metaData?.total,
              }}
            />
          </div>
        </>
      }
    />
  );
};

export default AuditReport;
